import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { jwtDecode } from 'jwt-decode';

const AuthContext = createContext(null);

const storageKeys = {
  token: 'auth.token',
  username: 'auth.username',
  roles: 'auth.roles',
  expiresAt: 'auth.expiresAt',
};

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(localStorage.getItem(storageKeys.token) || null);
  const [username, setUsername] = useState(localStorage.getItem(storageKeys.username) || null);
  const [roles, setRoles] = useState(() => {
    const raw = localStorage.getItem(storageKeys.roles);
    return raw ? JSON.parse(raw) : [];
  });
  const [expiresAt, setExpiresAt] = useState(localStorage.getItem(storageKeys.expiresAt) || null);

  useEffect(() => {
    if (token) localStorage.setItem(storageKeys.token, token); else localStorage.removeItem(storageKeys.token);
  }, [token]);
  useEffect(() => {
    if (username) localStorage.setItem(storageKeys.username, username); else localStorage.removeItem(storageKeys.username);
  }, [username]);
  useEffect(() => {
    if (roles?.length) localStorage.setItem(storageKeys.roles, JSON.stringify(roles)); else localStorage.removeItem(storageKeys.roles);
  }, [roles]);
  useEffect(() => {
    if (expiresAt) localStorage.setItem(storageKeys.expiresAt, expiresAt); else localStorage.removeItem(storageKeys.expiresAt);
  }, [expiresAt]);

  const isExpired = useMemo(() => {
    if (!expiresAt) return false;
    const now = Date.now();
    const exp = new Date(expiresAt).getTime();
    return now >= exp;
  }, [expiresAt]);

  const setSession = ({ token: newToken, username: newUsername, roles: newRoles, expiresAt: newExpiresAt }) => {
    setToken(newToken || null);
    setUsername(newUsername || null);
    setRoles(Array.isArray(newRoles) ? newRoles : []);
    setExpiresAt(newExpiresAt || null);
  };

  const clearSession = () => setSession({ token: null, username: null, roles: [], expiresAt: null });

  const value = useMemo(() => ({
    token,
    username,
    roles,
    expiresAt,
    isAuthenticated: Boolean(token) && !isExpired,
    hasRole: (role) => roles?.includes(role),
    setSession,
    clearSession,
    decoded: token ? jwtDecode(token) : null,
  }), [token, username, roles, expiresAt, isExpired]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};


